import * as React from "react"
import * as TooltipPrimitive from "@radix-ui/react-tooltip"

import { cn } from "@/lib/utils"

const TooltipProvider = TooltipPrimitive.Provider
const TooltipRoot = TooltipPrimitive.Root
const TooltipTrigger = TooltipPrimitive.Trigger

// CSS-driven animations via Tailwind ``data-[state=open]/closed`` /
// ``data-[side=*]`` classes — no framer-motion / AnimatePresence here.
//
// The previous implementation wrapped a ``<motion.div>`` inside
// ``<TooltipPrimitive.Content asChild>`` inside ``<AnimatePresence>``.
// That stacked Radix's Slot, framer-motion's animation child, and
// AnimatePresence's presence tracking — which on tooltip open
// (after ``delayDuration`` setTimeout) produced
//
//   React.Children.only expected to receive a single React element child.
//
// The crash was specifically in Radix's SlotClone count-check when its
// effective ``children`` resolved to more than one item — most likely
// because ``{...props}`` re-introduced a ``children`` prop alongside
// the JSX-nested motion.div. Tailwind's animate-in / fade-in-* /
// zoom-in-95 / slide-in-from-* classes are the native Radix pattern
// and produce the same visual effect without any Slot indirection.
const TooltipContent = React.forwardRef<
  React.ElementRef<typeof TooltipPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Content>
>(({ className, sideOffset = 4, ...props }, ref) => (
  <TooltipPrimitive.Content
    ref={ref}
    sideOffset={sideOffset}
    // Above the floating panels (notification center, ticket panel: 15000)
    // and the toasts (15010): a rail tooltip next to an open panel was hidden behind it.
    className={cn(
      "z-[15020] overflow-hidden rounded-md px-3 py-1.5 text-xs",
      "bg-popover text-popover-foreground",
      "backdrop-blur-sm",
      "shadow-lg border border-border",
      "animate-in fade-in-0 zoom-in-95",
      "data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95",
      "data-[side=bottom]:slide-in-from-top-2",
      "data-[side=left]:slide-in-from-right-2",
      "data-[side=right]:slide-in-from-left-2",
      "data-[side=top]:slide-in-from-bottom-2",
      className
    )}
    {...props}
  />
))
TooltipContent.displayName = TooltipPrimitive.Content.displayName

interface TooltipProps {
  children: React.ReactNode
  content: React.ReactNode
  side?: "top" | "right" | "bottom" | "left"
  align?: "start" | "center" | "end"
  sideOffset?: number
  delayDuration?: number
  className?: string
}

function Tooltip({
  children,
  content,
  side = "top",
  align,
  sideOffset = 4,
  delayDuration = 500,
  className,
}: TooltipProps) {
  return (
    <TooltipRoot delayDuration={delayDuration}>
      <TooltipTrigger asChild>{children}</TooltipTrigger>
      <TooltipContent side={side} align={align} sideOffset={sideOffset} className={className}>
        {content}
      </TooltipContent>
    </TooltipRoot>
  )
}

export {
  Tooltip,
  TooltipProvider,
  TooltipRoot,
  TooltipTrigger,
  TooltipContent,
}
